import { useState, useEffect, useCallback, useRef } from 'react';

// ============================================
// DEVICE SELECTION HOOK
// ============================================

export interface DeviceInfo {
  deviceId: string;
  label: string;
  kind: 'videoinput' | 'audioinput';
}

// Bound enumerateDevices() so a hung call (observed on the first permission grant
// while the OS is still releasing the capture device) can never strand
// isEnumerating=true — which gates the join flow forever.
//
// This bound is safe where the createLocalTracks one was not (A2): enumerateDevices
// never waits on a person. It resolves straight away whatever the permission state
// is — with blank labels when nothing has been granted — so the only thing that can
// hold it up is the OS. getUserMedia is the opposite: while the permission dialog is
// open it is pending on a human reading a question, which is why the capture timeout
// there is armed only once the permission is granted. Do not copy that arming here;
// there is nothing to wait for.
const ENUMERATE_TIMEOUT_MS = 4_000;

export interface DeviceSelection {
  /** Available cameras (empty until permissions granted and enumeration complete) */
  cameras: DeviceInfo[];
  /** Available microphones (empty until permissions granted and enumeration complete) */
  microphones: DeviceInfo[];
  /** Currently selected camera deviceId (null before enumeration completes) */
  selectedCamera: string | null;
  /** Currently selected microphone deviceId (null before enumeration completes) */
  selectedMicrophone: string | null;
  /** Whether multiple devices exist and selection is needed */
  needsSelection: boolean;
  /** Whether enumeration is in progress */
  isEnumerating: boolean;
  /** Set camera selection */
  selectCamera: (deviceId: string) => void;
  /** Set microphone selection */
  selectMicrophone: (deviceId: string) => void;
  /** Re-enumerate devices (for hot-plug detection) */
  refreshDevices: () => Promise<void>;
}

/**
 * Hook that manages media device enumeration and selection.
 *
 * Must be initialized after permissions are granted. Device labels are
 * empty strings before permission grant on most browsers.
 *
 * @param permissionGranted - Set to true after getUserMedia() succeeds.
 *   The hook does nothing until this is true.
 */
export function useDeviceSelection(permissionGranted: boolean): DeviceSelection {
  const [cameras, setCameras] = useState<DeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<DeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string | null>(null);
  const [needsSelection, setNeedsSelection] = useState(false);
  const [isEnumerating, setIsEnumerating] = useState(false);

  // Track whether the permission-grant effect has already fired its one-shot
  // enumeration. This guards only the auto-effect below — calling refreshDevices()
  // directly (e.g. from an in-page "Try Again") always re-enumerates, so retry
  // works without a full page reload.
  const hasEnumerated = useRef(false);

  // Monotonic sequence so concurrent refreshDevices() calls (auto-effect +
  // Try Again, or a slow earlier call racing a newer one) can't clobber each
  // other: only the latest call may apply its results / own the enumerating flag.
  const refreshSeq = useRef(0);

  const refreshDevices = useCallback(async (): Promise<void> => {
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
      return;
    }

    const seq = ++refreshSeq.current;
    setIsEnumerating(true);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      // Race enumeration against a timeout. The winning branch is wrapped so the
      // losing promise resolves harmlessly (no unhandled rejection). If the timeout
      // wins we degrade to empty device lists → the caller falls back to browser
      // default devices, and the `finally` still clears isEnumerating so the
      // connect/advance flow can proceed instead of hanging. The timeout handle is
      // cleared in `finally` so a fast win doesn't leak a live 4s timer.
      const enumeration = await Promise.race([
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices): { devices: MediaDeviceInfo[] } => ({ devices })),
        new Promise<{ devices: null }>((resolve) => {
          timeoutId = setTimeout(() => resolve({ devices: null }), ENUMERATE_TIMEOUT_MS);
        }),
      ]);

      // A newer refreshDevices() started while we were awaiting — discard our
      // (now stale) results so we can't overwrite the newer call's device list.
      if (seq !== refreshSeq.current) return;

      if (!enumeration.devices) {
        console.warn(
          '[Stream E] enumerateDevices() timed out; falling back to default devices'
        );
        setCameras([]);
        setMicrophones([]);
        setNeedsSelection(false);
        setSelectedCamera(null);
        setSelectedMicrophone(null);
        return;
      }

      const allDevices = enumeration.devices;

      // Filter to videoinput and audioinput, exclude empty deviceId (aggregate "default" devices)
      const videoDevices = allDevices.filter(
        (d) => d.kind === 'videoinput' && d.deviceId !== ''
      );
      const audioDevices = allDevices.filter(
        (d) => d.kind === 'audioinput' && d.deviceId !== ''
      );

      // Map to DeviceInfo with fallback labels.
      // macOS/Chrome exposes the system default twice (deviceId "default" and
      // "communications"), both pointing to the same physical mic. Annotate the
      // labels so the user sees "(default)" / "(default communications)" instead
      // of what looks like four distinct microphones.
      const annotate = (d: MediaDeviceInfo): string => {
        const base = d.label;
        if (d.deviceId === 'default') return `${base} (default)`;
        if (d.deviceId === 'communications') return `${base} (default communications)`;
        return base;
      };

      const mappedCameras: DeviceInfo[] = videoDevices.map((d, index) => ({
        deviceId: d.deviceId,
        label: annotate(d) || `Camera ${index + 1}`,
        kind: 'videoinput',
      }));

      const mappedMicrophones: DeviceInfo[] = audioDevices.map((d, index) => ({
        deviceId: d.deviceId,
        label: annotate(d) || `Microphone ${index + 1}`,
        kind: 'audioinput',
      }));

      setCameras(mappedCameras);
      setMicrophones(mappedMicrophones);

      // Determine if user needs to make a selection
      const multipleCamera = mappedCameras.length > 1;
      const multipleMic = mappedMicrophones.length > 1;
      setNeedsSelection(multipleCamera || multipleMic);

      // Auto-select or pre-select first device of each type
      if (mappedCameras.length > 0) {
        setSelectedCamera((prev) => {
          // Keep current selection if still valid (device still present after hot-plug)
          const stillValid = prev && mappedCameras.some((c) => c.deviceId === prev);
          return stillValid ? prev : mappedCameras[0].deviceId;
        });
      } else {
        setSelectedCamera(null);
      }

      if (mappedMicrophones.length > 0) {
        setSelectedMicrophone((prev) => {
          const stillValid = prev && mappedMicrophones.some((m) => m.deviceId === prev);
          return stillValid ? prev : mappedMicrophones[0].deviceId;
        });
      } else {
        setSelectedMicrophone(null);
      }
    } finally {
      // Clear the race timeout so a fast enumerateDevices() win doesn't leave a
      // live 4s timer dangling.
      clearTimeout(timeoutId);
      // Only the latest call owns the flag; a superseded call must not flip it off
      // while a newer enumeration is still in flight.
      if (seq === refreshSeq.current) {
        setIsEnumerating(false);
      }
    }
  }, []);

  // Enumerate devices when permission is granted
  useEffect(() => {
    if (!permissionGranted) return;
    if (hasEnumerated.current) return;

    hasEnumerated.current = true;
    refreshDevices();
  }, [permissionGranted, refreshDevices]);

  // Listen for hot-plugged/removed devices
  useEffect(() => {
    if (!permissionGranted) return;
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.addEventListener !== 'function') {
      return;
    }

    const handleDeviceChange = () => {
      refreshDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [permissionGranted, refreshDevices]);

  const selectCamera = useCallback((deviceId: string) => {
    setSelectedCamera(deviceId);
  }, []);

  const selectMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
  }, []);

  return {
    cameras,
    microphones,
    selectedCamera,
    selectedMicrophone,
    needsSelection,
    isEnumerating,
    selectCamera,
    selectMicrophone,
    refreshDevices,
  };
}
